import React from 'react';
import PropTypes from 'prop-types';
import styles from '../AboutLocation.module.scss';
import { SIDEBAR_VIEWS, STATES_DATA } from '../../../../lib/constants';
import LayersOutline from '../../../../assets/svg/LayersOutline';
import ghAnalytics from '../../../../lib/analytics';
import InfoButton from '../../../InfoButton';
import LayerCategoryListItem from '../../Layers/LayerCategoryListItem';

const MAX_AVAILABLE_LAYERS = 5;

// Priority order for the "Available Layers" list.
const AVAILABLE_LAYERS_CATEGORY_PRIORITY = [
  'Hunt Units',
  'Hunt Zones',
  'Hunt Areas',
  'Government Land',
  'Private Land',
  'Roads and Trails',
  'Wildfires',
];

const TOOLTIP_AVAILABLE = 'available_layers';
const TOOLTIP_TEXT = 'Activate these additional layers to view more information about the location you\'re viewing';

function AvailableLayersSection({ isLoading, availableCategories, stateName, assetsUrl, dispatch, userProfile, openTooltip, onToggleTooltip }) {
  const hasCategories = availableCategories && availableCategories.length > 0;

  const stateAbbrev = STATES_DATA[stateName]?.abbrev?.toLowerCase();
  const stateIconUrl = stateAbbrev ? `${assetsUrl}/states/icons/small/${stateAbbrev}.png` : null;

  const sorted = hasCategories
    ? [...availableCategories]
      .map((category, index) => ({ category, index }))
      .sort((a, b) => {
        const aName = (a.category?.categoryName || '').toLowerCase().trim();
        const bName = (b.category?.categoryName || '').toLowerCase().trim();

        // Non-hunt ordering by the explicit priority list; otherwise keep original order after prioritized items.
        const aPriority = AVAILABLE_LAYERS_CATEGORY_PRIORITY.findIndex((name) => name.toLowerCase() === aName);
        const bPriority = AVAILABLE_LAYERS_CATEGORY_PRIORITY.findIndex((name) => name.toLowerCase() === bName);
        const aIsPrioritized = aPriority !== -1;
        const bIsPrioritized = bPriority !== -1;

        if (aIsPrioritized && bIsPrioritized) return aPriority - bPriority;
        if (aIsPrioritized) return -1;
        if (bIsPrioritized) return 1;
        return a.index - b.index;
      })
      .map(({ category }) => category)
      .slice(0, MAX_AVAILABLE_LAYERS)
    : [];

  const handleViewAllLayers = () => {
    dispatch({
      type: 'layers/setSelectedLayerCategory',
      payload: { stateName, categoryName: null },
    });
    dispatch({
      type: 'app/gotoSidebarWithHistory',
      payload: SIDEBAR_VIEWS.LayerCategoryList,
    });
  };

  const handleCategoryClick = (categoryName) => {
    ghAnalytics.logAboutLocationLayerClick(
      categoryName.toLowerCase().replace(/\s+/g, '_')
    );
  };

  return (
    <div className={styles.sectionDivider}>
      <div className={styles.sectionDividerHeader}>
        <LayersOutline width={20} height={20} />
        <span>Available Layers</span>
        <InfoButton
          tooltipKey={TOOLTIP_AVAILABLE}
          tooltipText={TOOLTIP_TEXT}
          openTooltip={openTooltip}
          onToggle={onToggleTooltip}
        />
      </div>

      {isLoading ? (
        <div className={styles.loadingCard}>
          <div className={styles.loadingSpinner} />
          <span>Checking all available layers</span>
        </div>
      ) : hasCategories ? (
        <>
          <div className={styles.availableLayersList}>
            {sorted.map((category, i) => (
              <div key={i} onClick={() => handleCategoryClick(category.categoryName)}>
                <LayerCategoryListItem
                  category={category}
                  categoryIsOpen={false}
                  categoryName={category.categoryName}
                  dispatch={dispatch}
                  stateName={stateName}
                  userProfile={userProfile}
                />
              </div>
            ))}
          </div>
          {stateName && (
            <button className={styles.viewAllLayersButton} onClick={handleViewAllLayers}>
              {stateIconUrl && (
                <img src={stateIconUrl} width={18} height={18} className={styles.viewAllLayersStateIcon} alt={stateName} />
              )}
              View all {stateName} layers
            </button>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>No layers are available for the selected area.</div>
      )}
    </div>
  );
}

AvailableLayersSection.propTypes = {
  availableCategories: PropTypes.array,
  assetsUrl: PropTypes.string,
  dispatch: PropTypes.func,
  isLoading: PropTypes.bool,
  onToggleTooltip: PropTypes.func,
  openTooltip: PropTypes.string,
  stateName: PropTypes.string,
  userProfile: PropTypes.object,
};

export default AvailableLayersSection;
